import { useEffect, useState, useCallback } from "react";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Trash2, MoreHorizontal, Pencil, X } from "lucide-react";

export default function Recipes() {
  const { can } = useAuth();
  const { options, outletOptions } = useMeta();
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "", sell_price: "", outlet_id: "", ingredients: [] });
  const [delTarget, setDelTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, it] = await Promise.all([api.get("/recipes"), api.get("/items", { params: { page_size: 500 } })]);
      setRows(r.data.items || []);
      setItems(it.data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", category: "Food", sell_price: "", outlet_id: outletOptions[0]?.value || "", ingredients: [] }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, category: r.category || "Food", sell_price: r.sell_price, outlet_id: r.outlet_id, ingredients: r.ingredients || [] }); setOpen(true); };

  const cost = (r) => (r.ingredients || []).reduce((s, i) => s + Number(i.qty || 0) * Number(i.cost_price || 0), 0);
  const fcPct = (r) => { const c = cost(r); return r.sell_price > 0 ? Math.round((c / r.sell_price) * 100) : 0; };

  const addIng = () => setForm((s) => ({ ...s, ingredients: [...s.ingredients, { item_id: "", item_name: "", qty: "", unit: "", cost_price: 0 }] }));
  const setIng = (idx, item_id) => {
    const it = items.find((x) => x.id === item_id);
    setForm((s) => ({ ...s, ingredients: s.ingredients.map((ing, i) => i === idx ? { ...ing, item_id, item_name: it?.name, unit: it?.unit, cost_price: it?.cost_price } : ing) }));
  };
  const setQty = (idx, qty) => setForm((s) => ({ ...s, ingredients: s.ingredients.map((ing, i) => i === idx ? { ...ing, qty } : ing) }));
  const rmIng = (idx) => setForm((s) => ({ ...s, ingredients: s.ingredients.filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, sell_price: Number(form.sell_price), ingredients: form.ingredients.filter((i) => i.item_id).map((i) => ({ ...i, qty: Number(i.qty) })) };
      if (editing) await api.put(`/recipes/${editing.id}`, payload);
      else await api.post("/recipes", payload);
      toast.success(editing ? "Recipe updated" : "Recipe created");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    try { await api.delete(`/recipes/${delTarget.id}`); toast.success("Deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(apiError(e)); setDelTarget(null); }
  };

  const formCost = form.ingredients.reduce((s, i) => s + Number(i.qty || 0) * Number(i.cost_price || 0), 0);

  const columns = [
    { key: "name", label: "Menu Item", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "category", label: "Category" },
    { key: "ingredients", label: "Ingredients", render: (r) => `${(r.ingredients || []).length} items` },
    { key: "sell_price", label: "Sell Price", className: "text-right", cellClassName: "tabular text-right", render: (r) => money(r.sell_price) },
    { key: "cost", label: "Food Cost", className: "text-right", cellClassName: "tabular text-right", render: (r) => money(cost(r)) },
    { key: "fc", label: "Food Cost %", className: "text-right", cellClassName: "text-right", render: (r) => {
      const p = fcPct(r);
      return <span className={`font-semibold tabular ${p > 35 ? "text-red-600" : p > 28 ? "text-amber-600" : "text-emerald-600"}`}>{p}%</span>;
    } },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Recipe / Food Cost" subtitle="BOM per menu item with theoretical cost and food-cost %.">
        {can("recipes:create") && <Button onClick={openNew} data-testid="add-recipe-btn"><Plus className="mr-1 h-4 w-4" /> New Recipe</Button>}
      </PageHeader>

      <DataTable columns={columns} rows={rows} loading={loading} total={rows.length}
        emptyTitle="No recipes" emptyHint="Define recipes to compute food cost and variance."
        rowActions={(r) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`recipe-menu-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {can("recipes:edit") && <DropdownMenuItem onClick={() => openEdit(r)}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>}
              {can("recipes:delete") && <DropdownMenuItem className="text-destructive" onClick={() => setDelTarget(r)}><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="recipe-form">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Recipe" : "New Recipe"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="eyebrow">Menu Item</Label><Input data-testid="recipe-name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} required /></div>
              <div className="space-y-1.5"><Label className="eyebrow">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
                  <SelectTrigger data-testid="recipe-cat"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>{options("sales_category").map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="eyebrow">Sell Price</Label><Input data-testid="recipe-price" type="number" step="0.01" value={form.sell_price} onChange={(e) => setForm((s) => ({ ...s, sell_price: e.target.value }))} required /></div>
              <div className="space-y-1.5"><Label className="eyebrow">Outlet</Label>
                <Select value={form.outlet_id} onValueChange={(v) => setForm((s) => ({ ...s, outlet_id: v }))}>
                  <SelectTrigger data-testid="recipe-outlet"><SelectValue placeholder="Outlet" /></SelectTrigger>
                  <SelectContent>{outletOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-sm border border-border">
              <div className="flex items-center justify-between border-b border-border bg-secondary/50 px-3 py-2">
                <span className="eyebrow">Ingredients</span>
                <Button type="button" size="sm" variant="outline" onClick={addIng} data-testid="add-ingredient"><Plus className="mr-1 h-3.5 w-3.5" />Add</Button>
              </div>
              <div className="space-y-2 p-3">
                {form.ingredients.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">No ingredients added.</p>}
                {form.ingredients.map((ing, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Select value={ing.item_id} onValueChange={(v) => setIng(idx, v)}>
                      <SelectTrigger className="flex-1" data-testid={`ing-item-${idx}`}><SelectValue placeholder="Select item" /></SelectTrigger>
                      <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="w-24" type="number" step="0.01" placeholder="Qty" value={ing.qty} onChange={(e) => setQty(idx, e.target.value)} data-testid={`ing-qty-${idx}`} />
                    <span className="w-12 text-xs text-muted-foreground">{ing.unit}</span>
                    <span className="w-20 text-right text-sm tabular">{money(Number(ing.qty || 0) * Number(ing.cost_price || 0))}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => rmIng(idx)}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between border-t border-border px-3 py-2 text-sm">
                <span>Total food cost</span>
                <span className="font-semibold tabular">{money(formCost)} {form.sell_price > 0 && <span className="ml-2 text-muted-foreground">({Math.round((formCost / Number(form.sell_price)) * 100)}%)</span>}</span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="recipe-submit">{busy ? "Saving…" : "Save recipe"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)} title="Delete recipe?" description="This cannot be undone." confirmText="Delete" destructive onConfirm={remove} />
    </div>
  );
}
